import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	effect,
	inject,
	input,
	OnInit,
	signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ContentService, ContentUrlPipe } from '@context/frontend/content';
import { getUaDevice } from '@context/frontend/native/platform';
import { Content } from '@context/shared/types/common';
import { TuiHint, TuiIcon, TuiLink, TuiLoader } from '@taiga-ui/core';

/** Builds the router link for a breadcrumb segment, overriding the default
 * portal `contentUrl` behaviour (used by e.g. the external viewer). */
export type BreadcrumbLinkFactory = (
	segment: Content,
) => unknown[] | string | null;

/**
 * Breadcrumbs are currently only needed for Folder Structure so this component
 * is going to be heavily integrated with the `ContentFolder` type.
 *
 * @todo eventually rethink how to make this more generic for any entity and breadcrumb instance
 */
@Component({
	imports: [
		TuiLink,
		TuiIcon,
		RouterLink,
		ContentUrlPipe,
		NgTemplateOutlet,
		TuiLoader,
		TuiHint,
	],
	selector: 'ctx-breadcrumbs',
	templateUrl: 'breadcrumbs.component.html',
	styleUrl: 'breadcrumbs.component.scss',
	host: {
		'[attr.device]': 'device',
		'[attr.disabled]': 'disabled()',
		'[attr.path-overflow]': 'overflow()',
	},
})
export class BreadcrumbsComponent implements OnInit {
	readonly device = getUaDevice();

	readonly content = input.required<Content>();

	/**
	 * Pre-built breadcrumb path. When provided, the component renders these
	 * segments directly and never calls `generateFolderPath` - useful for
	 * contexts (like the external viewer) that compute a bounded path of their
	 * own and may not have full read access to every ancestor.
	 */
	readonly segments = input<Content[] | null>(null);

	/**
	 * Optional router-link builder. When provided it replaces the default portal
	 * `contentUrl` link, letting other shells (e.g. the external viewer) route
	 * breadcrumbs into their own URL space.
	 */
	readonly linkFor = input<BreadcrumbLinkFactory | null>(null);

	readonly showHome = input(true);

	/**
	 * `true` if the breadcrumbs should show the content that was provided
	 * to generate the path.
	 *
	 * i.e. wanting to show the file being passed from the media details page.
	 */
	readonly showContent = input(false);

	/**
	 * `true` if the breadcrumbs should not be interacted with and show
	 * in a disabled state. This was intended for archive detail views where the
	 * path of the content should not be interacted with, but needed to see for
	 * information
	 */
	readonly disabled = input(false);

	/**
	 * `true` if the breadcrumbs should show the archived content path.
	 */
	readonly archived = input(false);

	readonly homePath = {
		name: 'Home',
		type: 'folder',
	} as Content;

	readonly archivedPath = {
		name: 'Archive',
		type: 'folder',
	} as Content;

	readonly path = signal<Content[]>([]);

	readonly overflow = signal(false);
	readonly loading = signal(false);

	private readonly contentService = inject(ContentService);

	constructor() {
		// Keep the rendered path in sync with an externally-supplied segment list.
		effect(() => {
			const segments = this.segments();
			if (segments == null) return;
			this.path.set(segments);
			this.overflow.set(segments.length > 1);
		});
	}

	ngOnInit() {
		// Only resolve the path from the content service when no explicit
		// segments were supplied.
		if (this.segments() == null) this.createPath();
	}

	createPath() {
		if (this.loading()) return;
		this.loading.set(true);
		const contentValue = this.content();
		this.contentService
			.generateFolderPath(contentValue)
			.then((res) => {
				this.path.set(res ?? []);
				this.overflow.set(this.path().length > 1);

				if (this.showContent()) {
					this.path.update((path) => [...path, contentValue]);
				}
			})
			.finally(() => {
				this.loading.set(false);
			});
	}
}
